import { EdgeClientFlags } from './edge-client-flags';
import { EdgeExperimentPolicies } from './edge-experiment-policies';
import type { ExperimentPolicy } from './flag-types';

/**
 * Which call site is allowed to emit an experiment's exposure event.
 *
 * - `page-load`: `applyEdgeFlags` emits it while applying the request's edge
 *   flags — where every live experiment's exposure has always fired.
 * - `deferred`: nothing fires until app code calls `fireExposure(label)` at
 *   the declared moment, so a marketing pageview cannot put the visitor in
 *   the denominator.
 *
 * See docs/projects/active/edge-experiments-account-pinned-bucketing.md §3.6
 * and decision C5.
 */
export type ExposureTrigger = 'page-load' | 'deferred';

/**
 * The union of every `exposureAt` label a live experiment declares. Typing
 * `fireExposure` to this makes a typo'd label a compile error instead of a
 * silently empty denominator discovered weeks later.
 */
export type PolicyExposureLabel =
  (typeof EdgeExperimentPolicies)[keyof typeof EdgeExperimentPolicies]['exposureAt'];

/**
 * Every `exposureAt` label an experiment may declare, and the call site that
 * owns it.
 *
 * `satisfies Record<PolicyExposureLabel, ExposureTrigger>` is the discipline
 * that keeps this from drifting: an experiment cannot declare a label without
 * also stating where it fires, the same way `EdgeExperimentPolicies` cannot
 * omit an edge flag. The two funnel labels are `page-load` because that
 * describes today's behaviour exactly — `applyEdgeFlags` on the landing
 * pageview — and flipping one to `deferred` would silently empty a running
 * experiment's denominator.
 *
 * `paywall-view` is the opposite case and the reason `deferred` exists.
 * `pricing-test-v1` runs on CDN-cached, SEO-indexed marketing surfaces, so
 * firing on their pageviews would fill the denominator with organic readers
 * who never reach a checkout. It stays silent until the paywall itself calls
 * `fireExposure('paywall-view')`.
 */
const ExposureTriggerByLabel = {
  'academic-landing-view': 'page-load',
  'homepage-view': 'page-load',
  'paywall-view': 'deferred',
} as const satisfies Record<PolicyExposureLabel, ExposureTrigger>;

/**
 * PostHog flag key → the `exposureAt` label its experiment declared.
 *
 * The policies are keyed by flag NAME and the runtime `EdgeFlags` map by
 * PostHog KEY, so the two registries are joined once here.
 */
const exposureLabelByFlagKey = buildExposureLabelsByFlagKey();

function buildExposureLabelsByFlagKey(): Map<string, string> {
  const policies: Record<string, ExperimentPolicy | undefined> =
    EdgeExperimentPolicies;
  const labels = new Map<string, string>();
  for (const [flagName, def] of Object.entries(EdgeClientFlags)) {
    const policy = policies[flagName];
    if (policy) labels.set(def.key, policy.exposureAt);
  }
  return labels;
}

/** The `exposureAt` label declared for an edge flag, by its PostHog key. */
export function exposureLabelForFlagKey(flagKey: string): string | undefined {
  return exposureLabelByFlagKey.get(flagKey);
}

/**
 * Where this flag's exposure event is allowed to fire.
 *
 * A key with no policy is not an edge experiment this build knows about — a
 * stale `x-variants` entry from a removed one, or a test fixture — and keeps
 * firing at page load, so nothing observable changes for it. A policied key
 * fires at page load only if its label says so; an unregistered label cannot
 * exist in a compiling build, and defaults to `deferred` because a label
 * nobody wired up must not quietly collect pageviews.
 */
export function exposureTriggerForFlagKey(flagKey: string): ExposureTrigger {
  const label = exposureLabelByFlagKey.get(flagKey);
  if (label === undefined) return 'page-load';
  const triggers: Record<string, ExposureTrigger | undefined> =
    ExposureTriggerByLabel;
  return triggers[label] ?? 'deferred';
}
